
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useTranslations } from "use-intl";
import { LanguageSection } from "@/components/i18n/LanguageSection";

type Profile = {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  personal_number: string | null;
  avatar_url: string | null;
  updated_at: string;
};

const Profile = () => {
  const t = useTranslations("profile");
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [personalNumber, setPersonalNumber] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const getProfile = async () => {
      try {
        setLoading(true);
        
        if (!user) return;

        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) {
          throw error;
        }

        if (data) {
          setProfile(data);
          setFirstName(data.first_name || "");
          setLastName(data.last_name || "");
          setPersonalNumber(data.personal_number || "");
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };

    getProfile();
  }, [user]);

  // Its own effect so the message follows the language without refetching the profile (which would
  // overwrite what the user has typed); a fixed id keeps a language switch from stacking a second toast.
  useEffect(() => {
    if (loadFailed) toast.error(t("toasts.loadFailed"), { id: "profile-load-failed" });
  }, [loadFailed, t]);

  const updateProfile = async () => {
    try {
      setUpdating(true);
      
      if (!user) return;

      // Validate personal number if provided
      if (personalNumber) {
        const personalNumberPattern = /^[0-9]{12}$/;
        if (!personalNumberPattern.test(personalNumber)) {
          toast.error(t("toasts.personalNumberInvalid"));
          setUpdating(false);
          return;
        }
      }

      const updates = {
        id: user.id,
        first_name: firstName,
        last_name: lastName,
        personal_number: personalNumber || null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user.id);

      if (error) {
        throw error;
      }

      toast.success(t("toasts.saved"));
      
      // Update the local profile state
      setProfile(prev => {
        if (!prev) return null;
        return { ...prev, first_name: firstName, last_name: lastName, personal_number: personalNumber };
      });
      
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(t("toasts.saveFailed"));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-2xl py-10">
      <h1 className="text-2xl font-bold mb-6">{t("title")}</h1>
      
      <Card>
        <CardHeader>
          <CardTitle>{t("personal.title")}</CardTitle>
          <CardDescription>{t("personal.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t("fields.email")}</Label>
            <Input id="email" value={user?.email || ""} disabled />
            <p className="text-sm text-muted-foreground">{t("fields.emailLocked")}</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="firstName">{t("fields.firstName")}</Label>
            <Input 
              id="firstName" 
              value={firstName} 
              onChange={(e) => setFirstName(e.target.value)} 
              placeholder={t("fields.firstNamePlaceholder")} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="lastName">{t("fields.lastName")}</Label>
            <Input 
              id="lastName" 
              value={lastName} 
              onChange={(e) => setLastName(e.target.value)} 
              placeholder={t("fields.lastNamePlaceholder")} 
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="personalNumber">{t("fields.personalNumber")}</Label>
            <Input 
              id="personalNumber" 
              value={personalNumber} 
              onChange={(e) => {
                // Only allow digits and limit to 12
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 12) {
                  setPersonalNumber(value);
                }
              }}
              placeholder="123456789012" 
              maxLength={12}
            />
            <p className="text-sm text-muted-foreground">
              {t("fields.personalNumberHint")}
            </p>
          </div>
        </CardContent>
        <CardFooter>
          <Button 
            onClick={updateProfile} 
            disabled={updating}
            className="w-full"
          >
            {updating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("actions.saving")}
              </>
            ) : (
              t("actions.save")
            )}
          </Button>
        </CardFooter>
      </Card>

      <Card className="mt-6">
        <CardContent className="pt-6">
          <LanguageSection />
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
